import { TUser } from "@/types";


/**
 * The account fields `User.RequestUserDto` accepts and `UserServiceImplementor#updateUser`
 * writes back onto the user row.
 */
export type TAccountFields = {
    salutation: string
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    clientType: string
    gender: string
    nationality: string
    identificationType: string
    identificationNumber: string
    dateOfBirth: string
}

/** The columns of the linked `business` row, named as `BusinessRequestDto` expects them. */
export type TBusinessFields = {
    businessName: string
    businessTradingName: string
    businessTinNumber: string
    businessAddress: string
    businessRegion: string
    businessDistrict: string
    businessStreet: string
    businessPhone: string
    businessEmail: string
    businessManager: string
    businessLat: string
    businessLng: string
}

export type TAccountChanges = {
    account?: Partial<TAccountFields>
    business?: Partial<TBusinessFields>
}

const ACCOUNT_FIELDS: (keyof TAccountFields)[] = [
    "salutation",
    "firstName",
    "lastName",
    "email",
    "phoneNumber",
    "clientType",
    "gender",
    "nationality",
    "identificationType",
    "identificationNumber",
    "dateOfBirth"
]

const BUSINESS_FIELDS: (keyof TBusinessFields)[] = [
    "businessName",
    "businessTradingName",
    "businessTinNumber",
    "businessAddress",
    "businessRegion",
    "businessDistrict",
    "businessStreet",
    "businessPhone",
    "businessEmail",
    "businessManager",
    "businessLat",
    "businessLng"
]

/**
 * Builds the multipart body for `PUT /api/v1/clients/{id}`.
 *
 * `UserServiceImplementor#updateUser` assigns *every* field of the request onto the user
 * unconditionally - including `user.setBusiness(Business.fromDto(request.getBusiness()))` -
 * so whatever a request leaves out is written back as null. A settings page that posted only
 * the fields it owns would therefore wipe the rest of the account. Each page instead sends the
 * whole account, with only the fields it edits replaced.
 *
 * Files are deliberately never attached here: `updateUser` stores an uploaded `profileImage`
 * on the column that `User#getProfileImage()` overrides (it reads the `user_images` rows
 * instead) so the picture would never come back, and its `identificationImages` branch
 * discards the freshly uploaded files and re-uploads the stored URLs as base64. Neither
 * upload can be persisted until the backend is fixed.
 */
export function buildAccountFormData(user: TUser, changes: TAccountChanges = {}): FormData {
    const formData = new FormData();

    const current: Partial<TAccountFields> = {
        salutation: user.salutation,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        // `updateUser` falls back to `mobile` only when `phoneNumber` is absent, and the DTO
        // fills both from the same column.
        phoneNumber: user.phoneNumber ?? user.mobile,
        clientType: user.clientType,
        gender: user.gender,
        nationality: user.nationality,
        identificationType: user.identificationType,
        identificationNumber: user.identificationNumber,
        dateOfBirth: user.dateOfBirth
    }

    ACCOUNT_FIELDS.forEach((field) => {
        const value = changes.account && field in changes.account
            ? changes.account[field]
            : current[field];

        if (value) formData.append(field, value);
    })

    const business = user.business;

    if (business || changes.business) {
        BUSINESS_FIELDS.forEach((field) => {
            const value = changes.business && field in changes.business
                ? changes.business[field]
                : business?.[field];

            if (value) formData.append(`business.${field}`, value.toString());
        })
    }

    return formData;
}
